/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useId } from "react";
import { BEM } from "@adapptio/lib-components";
import { FieldWrapper } from "../FieldWrapper";
import { Dnd, DndDropArea, IDndProps } from "../../core";

import "./FieldList.scss";

type DragProps = "onDragEnd" | "onDragUpdate" | "onDragStart";

export interface IFieldListProps
	extends Omit<React.ComponentPropsWithoutRef<"div">, DragProps>,
		Pick<IDndProps, DragProps> {
	children?: React.ReactNode;
	className?: string;
	listRef?: React.MutableRefObject<HTMLDivElement>;
}

export const FieldList: React.FC<IFieldListProps> = ({
	children,
	className,
	onDragEnd,
	onDragStart,
	onDragUpdate,
	draggable,
	listRef,
	...props
}) => {
	const baseClass = BEM("edt-field-list");
	const uid = useId();

	if (className) {
		baseClass.add(className);
	}

	if (draggable) {
		return (
			<FieldWrapper type="list">
				<Dnd onDragStart={onDragStart} onDragUpdate={onDragUpdate} onDragEnd={onDragEnd}>
					<DndDropArea ref={listRef} droppableId={uid}>
						{({ droppableRef, droppableProps, placeholder }) => (
							<div
								className={baseClass.toClassName()}
								ref={droppableRef}
								{...props}
								{...droppableProps}
							>
								{children}
								{placeholder}
							</div>
						)}
					</DndDropArea>
				</Dnd>
			</FieldWrapper>
		);
	}

	return (
		<FieldWrapper type="list">
			<div className={baseClass.toClassName()} ref={listRef} {...props}>
				{children}
			</div>
		</FieldWrapper>
	);
};
